import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LoadingButton from "../../../components/LoadingButton";

interface DocumentationPage {
  id: string;
  title: string;
  slug: string;
  content: string;
  parentId: string | null;
  sortOrder: number;
  icon: string | null;
  children?: DocumentationPage[];
}

interface DocumentationPageDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (page: Partial<DocumentationPage>) => Promise<void>;
  page?: DocumentationPage | null;
  pages: DocumentationPage[];
  loading?: boolean;
}

// Flatten the page tree for the parent dropdown
const flattenPages = (
  pages: DocumentationPage[],
  excludeId?: string
): { id: string; title: string }[] => {
  const result: { id: string; title: string }[] = [];

  const traverse = (pageList: DocumentationPage[], depth: number = 0) => {
    for (const page of pageList) {
      if (page.id !== excludeId) {
        result.push({
          id: page.id,
          title: "  ".repeat(depth) + page.title,
        });
        if (page.children) {
          traverse(page.children, depth + 1);
        }
      }
    }
  };

  traverse(pages);
  return result;
};

// Generate slug from title
const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export default function DocumentationPageDialog({
  open,
  onClose,
  onSave,
  page,
  pages,
  loading = false,
}: DocumentationPageDialogProps) {
  const theme = useTheme();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [parentId, setParentId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const isEditing = !!page;
  const flattenedPages = flattenPages(pages, page?.id);


  useEffect(() => {
    if (page) {
      setTitle(page.title);
      setSlug(page.slug);
      setContent(page.content || "");
      setParentId(page.parentId);
      setSortOrder(page.sortOrder);
      setSlugManuallyEdited(true);
    } else {
      setTitle("");
      setSlug("");
      setContent("");
      setParentId(null);
      setSortOrder(0);
      setSlugManuallyEdited(false);
    }
    setActiveTab(0);
  }, [page, open]);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    if (!slugManuallyEdited) {
      setSlug(generateSlug(newTitle));
    }
  };

  const handleSlugChange = (newSlug: string) => {
    setSlug(newSlug);
    setSlugManuallyEdited(true);
  };

  const handleClose = () => {
    setTitle("");
    setSlug("");
    setContent("");
    setParentId(null);
    setSortOrder(0);
    setSlugManuallyEdited(false);
    setActiveTab(0);
    onClose();
  };

  const handleSave = async () => {
    if (title.trim() && slug.trim()) {
      await onSave({
        ...(page?.id ? { id: page.id } : {}),
        title: title.trim(),
        slug: slug.trim(),
        content: content.trim(),
        parentId,
        sortOrder,
      });
      handleClose();
    }
  };

  const isValid = title.trim() && slug.trim();

  const selectedParent = flattenedPages.find((p) => p.id === parentId) || null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
        },
      }}
    >
      <DialogTitle>
        {isEditing ? "Edit Documentation Page" : "Create Documentation Page"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", pt: 2 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            sx={{ flex: 2 }}
            autoFocus
            disabled={loading}
            required
          />
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            sx={{ flex: 1 }}
            disabled={loading}
            required
            helperText="URL-friendly identifier"
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Autocomplete
            options={flattenedPages}
            getOptionLabel={(option) => option.title}
            value={selectedParent}
            onChange={(_, newValue) => setParentId(newValue?.id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Parent Page"
                placeholder="None (top-level)"
              />
            )}
            sx={{ flex: 2 }}
            disabled={loading}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <TextField
            label="Sort Order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
            sx={{ flex: 1 }}
            disabled={loading}
            helperText="Lower numbers appear first"
          />
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Edit" />
            <Tab label="Preview" />
          </Tabs>
        </Box>

        {activeTab === 0 ? (
          <TextField
            fullWidth
            multiline
            label="Content (Markdown)"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={loading}
            placeholder="Enter markdown content..."
            sx={{
              flex: 1,
              "& .MuiInputBase-root": {
                height: "100%",
                alignItems: "flex-start",
              },
              "& .MuiInputBase-input": {
                height: "100% !important",
                overflow: "auto !important",
                fontFamily: "monospace",
                fontSize: "0.875rem",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 2,
              "& h1": {
                fontSize: "2rem",
                fontWeight: 700,
                mb: 2,
                mt: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
                pb: 1,
              },
              "& h2": {
                fontSize: "1.5rem",
                fontWeight: 600,
                mt: 4,
                mb: 2,
              },
              "& h3": {
                fontSize: "1.25rem",
                fontWeight: 600,
                mt: 3,
                mb: 1.5,
              },
              "& p": {
                mb: 2,
                lineHeight: 1.7,
              },
              "& ul, & ol": {
                mb: 2,
                pl: 3,
              },
              "& li": {
                mb: 0.5,
                lineHeight: 1.7,
              },
              "& code": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[800]
                    : theme.palette.grey[100],
                color: theme.palette.text.primary,
                padding: "2px 6px",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: "0.875rem",
              },
              "& pre": {
                backgroundColor: theme.palette.grey[900],
                color: theme.palette.grey[100],
                p: 2,
                borderRadius: 1,
                overflow: "auto",
                mb: 2,
                "& code": {
                  backgroundColor: "transparent",
                  padding: 0,
                  color: "inherit",
                },
              },
              "& table": {
                width: "100%",
                borderCollapse: "collapse",
                mb: 2,
              },
              "& th, & td": {
                border: `1px solid ${theme.palette.divider}`,
                p: 1.5,
                textAlign: "left",
              },
              "& th": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[800]
                    : theme.palette.grey[100],
                fontWeight: 600,
              },
              "& blockquote": {
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                pl: 2,
                ml: 0,
                color: theme.palette.text.secondary,
                fontStyle: "italic",
              },
              "& a": {
                color: theme.palette.primary.main,
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              },
            }}
          >
            {content ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            ) : (
              <Typography color="text.secondary" fontStyle="italic">
                No content to preview
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={loading}
          disabled={!isValid}
        >
          {isEditing ? "Save Changes" : "Create Page"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
